import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, ShieldCheck, ShoppingBag, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { FREE_SHIPPING_THRESHOLD, useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const [placing, setPlacing] = useState(false);

  const freeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;
  const total = subtotal;

  const goToShopifyCheckout = async () => {
    setPlacing(true);
    try {
      const { checkout_url } = await api.shopifyCheckout(
        items.map((i) => ({ slug: i.slug, variant_id: i.variant_id, qty: i.qty }))
      );
      clear();
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-32 text-center" data-testid="checkout-empty">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-neutral-100">
          <ShoppingBag size={24} className="text-neutral-400" />
        </div>
        <h1 className="mt-6 font-heading text-2xl font-bold">Your bag is empty</h1>
        <p className="mt-2 text-sm text-neutral-500">Add something useful before checking out.</p>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-neutral-900 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-700">
          Continue Shopping
        </Link>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
      <Reveal>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">Checkout</h1>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-neutral-500">
          <Lock size={13} /> Payment is completed on Shopify's secure, encrypted checkout
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-10 rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8" data-testid="order-summary">
          <h2 className="font-heading text-lg font-bold">Order Summary</h2>
          <div className="mt-4 space-y-4">
            {items.map((i) => (
              <div key={`${i.slug}-${i.variant_id || ""}`} className="flex items-center gap-3">
                <div className="relative shrink-0">
                  {i.image && <img src={i.image} alt={i.name} className="h-14 w-14 rounded-lg object-cover img-frame" />}
                  <span className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-neutral-900 text-[10px] font-semibold text-white">
                    {i.qty}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-neutral-700">{i.name}</p>
                  {i.variant_title && <p className="text-xs text-neutral-400">{i.variant_title}</p>}
                </div>
                <p className="text-sm font-semibold">{inr(i.price * i.qty)}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-2 border-t border-neutral-100 pt-4 text-sm">
            <div className="flex justify-between text-neutral-500">
              <span>Subtotal</span>
              <span className="font-medium text-neutral-900">{inr(subtotal)}</span>
            </div>
            <div className="flex justify-between text-neutral-500">
              <span>Shipping</span>
              <span className="font-medium text-neutral-900">{freeShipping ? "Free" : "Calculated at checkout"}</span>
            </div>
            <div className="flex justify-between border-t border-neutral-100 pt-3 font-heading text-lg font-bold">
              <span>Items total</span>
              <span data-testid="checkout-total">{inr(total)}</span>
            </div>
          </div>

          <button
            data-testid="place-order-button"
            onClick={goToShopifyCheckout}
            disabled={placing}
            className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-neutral-900 py-4 text-sm font-semibold text-white transition-colors hover:bg-neutral-700 disabled:opacity-60"
          >
            {placing ? "Connecting to Shopify..." : `Continue to Secure Checkout — ${inr(total)}`}
            {!placing && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
          </button>
          <p className="mt-3 text-center text-xs leading-relaxed text-neutral-400">
            You'll enter your delivery address and pay on Shopify's secure checkout. UPI, cards, net banking, and COD as
            enabled on the store.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs text-neutral-500">
          <span className="flex items-center gap-1.5"><ShieldCheck size={14} /> 7-day easy returns</span>
          <span className="flex items-center gap-1.5"><Truck size={14} /> Free shipping over ₹999</span>
          <span className="flex items-center gap-1.5"><Lock size={14} /> 256-bit encrypted payment</span>
        </div>
      </Reveal>
    </main>
  );
}
